import { DataView } from "@zebrabi/matrix-data";
import {
    type CellValue,
    DataBindingIds,
    DataHelperHost,
    OfficeDataHelper,
    PivotFieldCounts,
    PivotTableInput,
    RangeInput,
    type WorksheetRow,
} from "@zebrabi/office-data";
import { looksLikeCommentHeader } from "@zebrabi/data-helpers/fieldAssignment";
import { getTablesMetadata } from "@zebrabi/data-helpers/tablesMetadata";
import { isValidRangeInputData } from "@zebrabi/data-helpers/excelDataHelpers";
import { parseRowHierarchyDataView } from "@zebrabi/data-helpers/hierarchyParsing";
import { VISUAL_TYPE } from "./initialDefinitions";
import {
    dataViewToWorksheetRows,
    getNumberOfCategoryColumns,
    handleRepeatedCategories,
    isCrossTablesDataView,
    parseRangeDataView,
} from "./dataViewParsing";

/** What the tables data helper needs from its visual on top of the shared host: the saved cross-table state and its callbacks. */
export interface TablesDataHelperHost extends DataHelperHost<DataView> {
    /** True when the add-in was just inserted and has no saved settings yet. */
    isFirstInsert: boolean;
    /** The saved cross-table verdict, undefined when the saved settings predate it. */
    showCrossTables: boolean | undefined;
    /** Fired when the helper has written cross-table data (a Group column) to the worksheet. */
    onCrossTablesWritten: () => void;
    /** Fired when the helper decides the Excel range is a cross table, so the owner can save the verdict. May repeat. */
    onCrossTablesDetected: () => void;
    /** Fired when the helper found no data to read: an empty worksheet on insert, or a selection without a usable range. */
    onNoDataFound: () => void;
}

export class DataHelper extends OfficeDataHelper<DataView, TablesDataHelperHost> {
    protected readonly visualName = VISUAL_TYPE;
    protected readonly bindingIds: DataBindingIds = { pivotTable: "tablesPivotTableDataChangeEvent", range: "tablesRangeChanged" };
    protected readonly galleryName = "Tables gallery";

    private showCrossTables: boolean | undefined;
    private crossTablesWritten = false;

    /** @param host The visual that owns this helper: the live DataView, the saved state to start from and the callbacks. */
    constructor(host: TablesDataHelperHost) {
        super(host);
        this.showCrossTables = host.showCrossTables;
    }

    protected mountChooser() {
        this.host.onNoDataFound();
    }

    protected dataViewToRows(dataView: DataView): WorksheetRow[] {
        if (isCrossTablesDataView(dataView)) {
            this.crossTablesWritten = true;
            this.host.onCrossTablesWritten();
        }
        return dataViewToWorksheetRows(dataView);
    }

    protected getEmptyDataView(msg: string): DataView {
        return {
            matrix: null,
            rowFields: null,
            columnFields: null,
            valueFields: null,
            rowGrandTotal: null,
            columnGrandTotal: null,
            errorMessage: msg ? msg : "No pivot table found.",
            metadata: { columns: [] },
            rowNames: null,
            columnNames: null,
            valueNames: null,
        };
    }

    protected validatePivotFields({ rows, columns, values }: PivotFieldCounts): string | null {
        if (rows === 0 || values === 0) {
            return "Not enough fields. Please have at least one field in rows and one in values.";
        }
        if (columns > 2) {
            // empty columns has one hidden "column" Values
            return "Too many fields. Please have no more than one field in columns.";
        }
        return null;
    }

    protected parsePivotTable(input: PivotTableInput): DataView {
        const { rowFields, columnFields, valueFields, rowLabelsValues, columnLabelsValues, dataValues } = input;

        const dataView: DataView = {
            metadata: { columns: [] },
            matrix: null,
            rowFields: rowFields,
            columnFields: columnFields,
            valueFields: valueFields,
            rowGrandTotal: input.rowGrandTotal, // adds grand total columns, used for column parsing
            columnGrandTotal: input.columnGrandTotal, // adds grand total row, used for row parsing
            rowNames: rowFields,
            columnNames: columnFields,
            valueNames: valueFields,
        };

        // Fill the DataView metadata property
        getTablesMetadata(dataView, true, false);

        if (dataView.valueFields.length < 1 || dataView.rowFields.length < 1) {
            dataView.errorMessage = "Invalid data fields configuration or empty data";
            return dataView;
        }

        if (dataView.valueFields.length < 20) {
            if (dataView.columnFields.length < 2) {
                parseRowHierarchyDataView(dataView, rowLabelsValues, columnLabelsValues, dataValues, input.layout);
            } else {
                dataView.errorMessage = "Too many column (group) fields.";
            }
        } else {
            dataView.errorMessage = "Too many value fields.";
        }

        return dataView;
    }

    /**
     * Decides whether the Excel range is parsed as a cross table, and reports a new verdict through the owner.
     *
     * A cross table is forced by data the helper wrote to the worksheet itself, or remembered from the saved settings. Settings
     * saved before showCrossTables existed get the old rule once: more than four measures means a cross table. A first insert
     * has nothing saved, so nothing is migrated or reported.
     */
    private resolveCrossTables(measureCount: number): boolean {
        if (!this.host.isFirstInsert) {
            const legacyRuleSaysCross = this.showCrossTables === undefined && measureCount > 4;
            if (this.crossTablesWritten || legacyRuleSaysCross) {
                this.showCrossTables = true;
                this.host.onCrossTablesDetected();
            }
        }
        return this.crossTablesWritten || this.showCrossTables === true;
    }

    protected countCategoryColumns(range: Excel.Range): number {
        return getNumberOfCategoryColumns(range);
    }

    protected async parseRange({ context, range, headerRow, categoryColumnsCount, categoryLabelsValues }: RangeInput): Promise<DataView> {
        const columnHeaders: string[] = headerRow.map((v: CellValue) => String(v).trim());

        const categoryColumnsHeaders = columnHeaders.filter((h, i) => i < categoryColumnsCount);
        const measureColumnsHeaders = columnHeaders.filter((h, i) => i >= categoryColumnsCount);

        const categoryLabels = categoryColumnsCount > 1 ? handleRepeatedCategories(categoryLabelsValues, categoryColumnsCount) : [];

        const categoryFields =
            categoryColumnsHeaders.length === 0 || (categoryColumnsHeaders.length === 1 && categoryColumnsHeaders[0] === "")
                ? ["Category"]
                : categoryColumnsHeaders;

        let measureCount = measureColumnsHeaders.length;
        const hasCommentColumn = measureColumnsHeaders.some((c) => looksLikeCommentHeader(c)) || this.hasCommentsDataCol(range.valueTypes);
        if (hasCommentColumn) {
            measureCount--;
        }

        const isCrossTablesData = this.resolveCrossTables(measureCount);

        let columnFields = [];
        let valueFields = measureColumnsHeaders;

        if (isCrossTablesData) {
            // parse cross tables
            valueFields = ["Values"];
            columnFields = ["Group"];
        }

        const dataView: DataView = {
            metadata: { columns: [] },
            matrix: null,
            rowFields: categoryFields,
            columnFields: columnFields,
            valueFields: <any>valueFields,
            rowGrandTotal: false,
            columnGrandTotal: false,
            rowNames: this.host.isFirstInsert || categoryFields.length > 1 ? categoryFields : [], // keep the previous incorrect behavior for existing add-ins so that the formaulas are not lost
            columnNames: columnFields,
            valueNames: valueFields,
        };

        if (!isValidRangeInputData(range)) {
            dataView.errorMessage = this.getDataParsingErrorMsgHtml();
            return dataView;
        }

        getTablesMetadata(dataView, false, hasCommentColumn);

        const dataValuesRange = range.getOffsetRange(1, categoryColumnsCount).getResizedRange(-1, -categoryColumnsCount);
        dataValuesRange.load({ values: true, valueTypes: true });
        await context.sync();

        parseRangeDataView(
            dataView,
            categoryLabels?.length > 0 ? categoryLabels : categoryLabelsValues,
            <any>measureColumnsHeaders,
            dataValuesRange.values,
            dataValuesRange,
            range.values
        );

        return dataView;
    }
}
